import { CloseOutlined } from '@ant-design/icons';
import { useState } from 'react';
import styled from 'styled-components';

import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import { AttrNumberInput, AttrTextInput } from '@/components/Inputs';
import FlexibleTitleInput from '@/components/Inputs/FlexibleTitle';
import type { SelectOptionT } from '@/components/Select/types';
import { AttrText } from '@/components/Typography/NamedTypo';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import useCompanyStore from '@/features/Account/store';
import type { COMPANY_ACTIVE_STATUS } from '@/features/Account/types/constants';
import AgentDropdown from '@/features/Agent/components/AgentDropdown';
import AttrSelectDropdown from '@/features/Commons/Dropdown/AttrSelectDropdown';
import useCompanyAttributes from '@/hooks/domain/useCompanyAttributes';
import { useInputs } from '@/hooks/pure/useInputs';
import useKeymap from '@/hooks/pure/useKeymap';
import useStates from '@/hooks/pure/useStates';
import usePopupStore from '@/stores/pure/PopupStore';
import useToastStore from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { detailModalStyle } from '@/styles/style';
import { timeOut } from '@/utils/util';

type SelectState = {
  industry: string | null;
  size: string | null;
  plan: string | null;
  rating: string | null;
  account_type: string | null;
  account_stage: string | null;
  account_primary_usecase: string | null;
  country: string | null;
  status: COMPANY_ACTIVE_STATUS;
  agent: string | null;
};

type InputState = {
  name: string;
  employee_count: string;
  monthly_spend: string;
  annual_revenue: string;
  key: string;
};

const CreateCompanyModal = () => {
  const { addCompany, setCreateModalStatus, fetchCompanyList } =
    useCompanyStore();

  const updateWorkspaceFieldOptions = useWorkspaceStore(
    (state) => state.updateWorkspaceFieldOptions,
  );
  const setPopup = usePopupStore((state) => state.setPopup);

  const {
    industryOptions,
    sizeOptions,
    planOptions,
    ratingOptions,
    accountTypeOptions,
    accountStageOptions,
    accountPrimaryUsecseOptions,
    countryOptions,
    statusOptions,
  } = useCompanyAttributes();

  const [loading, setLoading] = useState(false);

  const { state, setValue } = useStates<SelectState>({
    industry: null,
    size: null,
    plan: null,
    rating: null,
    account_type: null,
    country: null,
    status: 1,
    agent: null,
    account_stage: null,
    account_primary_usecase: null,
  });

  const [inputs, onChange] = useInputs<InputState>({
    name: '',
    employee_count: '',
    monthly_spend: '',
    annual_revenue: '',
    key: '',
  });

  const closeModal = () => {
    setCreateModalStatus(false);
  };

  const { addDefaultToast } = useToastStore((state) => ({
    addDefaultToast: state.addDefaultToast,
  }));

  useKeymap('Escape', closeModal);

  const onSave = async () => {
    try {
      if (!inputs.name) throw Error('Name is blank');
      const req = {
        name: inputs.name,
        industry: state.industry || undefined,
        employee_count: inputs.employee_count
          ? Number(inputs.employee_count)
          : undefined,
        size: state.size || undefined,
        monthly_spend: inputs.monthly_spend
          ? Number(inputs.monthly_spend)
          : undefined,
        annual_revenue: inputs.annual_revenue
          ? Number(inputs.annual_revenue)
          : undefined,
        plan: state.plan || undefined,
        rating: state.rating || undefined,
        type: state.account_type || undefined,
        country: state.country || undefined,
        status: state.status,
        agent: state.agent || undefined,
        stage: state.account_stage || undefined,
        primary_usecase: state.account_primary_usecase || undefined,
        key: inputs.key ? String(inputs.key) : undefined,
      };
      setLoading(true);
      await addCompany(req);
      await timeOut(2000);
      await fetchCompanyList();
      setCreateModalStatus(false);
    } catch (e) {
      addDefaultToast('This account name or key already exists.', 'fail');
    } finally {
      setLoading(false);
    }
  };

  const handleOptionDelete = (
    option: SelectOptionT,
    target: WorkspaceConfigsFields,
    itemName: string,
  ) => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: `Delete the ${itemName}`,
      message: `Are you sure you want to delete this ${itemName}?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => {
        updateWorkspaceFieldOptions(4, target, option.searchName);
      },
    });
  };

  return (
    <ModalStyle status={true}>
      <ContentStyle>
        <Flex j="flex-end">
          <IconButton
            icon={<CloseOutlined />}
            onClick={closeModal}
            tooltip="Close"
            shortcut="ESC"
          />
        </Flex>
        <Space h="5px" />
        <FlexibleTitleInput
          placeholder="New Account"
          name="name"
          value={inputs.name}
          onChange={onChange}
        />
        <Space h="21px" />
        <Flex className="company-property-area">
          <PropertyRow>
            <AttrText className="property-label">Industry</AttrText>
            <AttrSelectDropdown
              name={`company-create-industry-dropdown`}
              view="create"
              selected={state.industry || null}
              options={industryOptions}
              onSelect={(industry) => setValue('industry', industry)}
              onCreate={async (optionName) => {
                await updateWorkspaceFieldOptions(1, 'industry', optionName);
                setValue('industry', optionName);
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'industry', 'Industry item')
              }
              onClear={() => setValue('industry', null)}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Employees</AttrText>
            <AttrNumberInput
              name={'employee_count'}
              value={inputs.employee_count}
              placeholder="Number of employees"
              onChange={onChange}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Size</AttrText>
            <AttrSelectDropdown
              name={`company-create-size-dropdown`}
              view="create"
              selected={state.size || null}
              options={sizeOptions}
              onSelect={(size) => setValue('size', size)}
              onCreate={async (optionName) => {
                await updateWorkspaceFieldOptions(1, 'size', optionName);
                setValue('size', optionName);
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'size', 'Size item')
              }
              onClear={() => setValue('size', null)}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Monthly spend</AttrText>
            <AttrNumberInput
              name={'monthly_spend'}
              value={inputs.monthly_spend}
              placeholder="Account's monthly subscription amount"
              onChange={onChange}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Annual revenue</AttrText>
            <AttrNumberInput
              name={'annual_revenue'}
              value={inputs.annual_revenue}
              placeholder="Account's annual revenue amount"
              onChange={onChange}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Plan</AttrText>
            <AttrSelectDropdown
              name={`company-create-plan-dropdown`}
              view="create"
              selected={state.plan || null}
              options={planOptions}
              onSelect={(plan) => setValue('plan', plan)}
              onCreate={async (optionName) => {
                await updateWorkspaceFieldOptions(1, 'plan', optionName);
                setValue('plan', optionName);
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'plan', 'Plan item')
              }
              onClear={() => setValue('plan', null)}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Rating</AttrText>
            <AttrSelectDropdown
              name={`company-create-rating-dropdown`}
              view="create"
              selected={state.rating || null}
              options={ratingOptions}
              onSelect={(rating) => setValue('rating', rating)}
              onCreate={async (optionName) => {
                await updateWorkspaceFieldOptions(1, 'rating', optionName);
                setValue('rating', optionName);
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'rating', 'Rating item')
              }
              onClear={() => setValue('rating', null)}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Type</AttrText>
            <AttrSelectDropdown
              name={`company-create-type-dropdown`}
              view="create"
              selected={state.account_type || null}
              options={accountTypeOptions}
              onSelect={(type) => setValue('account_type', type)}
              onCreate={async (optionName) => {
                await updateWorkspaceFieldOptions(
                  1,
                  'account_type',
                  optionName,
                );
                setValue('account_type', optionName);
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'account_type', 'Type item')
              }
              onClear={() => setValue('account_type', null)}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Stage</AttrText>
            <AttrSelectDropdown
              name={`company-create-stage-dropdown`}
              view="create"
              selected={state.account_stage || null}
              options={accountStageOptions}
              onSelect={(type) => setValue('account_stage', type)}
              onCreate={async (optionName) => {
                await updateWorkspaceFieldOptions(
                  1,
                  'account_stage',
                  optionName,
                );
                setValue('account_stage', optionName);
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'account_stage', 'Type item')
              }
              onClear={() => setValue('account_stage', null)}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Primary Usecase</AttrText>
            <AttrSelectDropdown
              name={`company-create-usecase-dropdown`}
              view="create"
              selected={state.account_primary_usecase || null}
              options={accountPrimaryUsecseOptions}
              onSelect={(type) =>
                setValue('account_primary_usecase', type)
              }
              onCreate={async (optionName) => {
                await updateWorkspaceFieldOptions(
                  1,
                  'account_primary_usecase',
                  optionName,
                );
                setValue('account_primary_usecase', optionName);
              }}
              onDelete={(option) =>
                handleOptionDelete(
                  option,
                  'account_primary_usecase',
                  'Type item',
                )
              }
              onClear={() => setValue('account_primary_usecase', null)}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Country</AttrText>
            <AttrSelectDropdown
              name={`company-create-country-dropdown`}
              view="create"
              selected={state.country || null}
              options={countryOptions}
              onSelect={(country) => setValue('country', country)}
              onClear={() => setValue('country', null)}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Active status</AttrText>
            <AttrSelectDropdown
              name={`company-create-status-dropdown`}
              view="create"
              selected={state.status?.toString() || null}
              options={statusOptions}
              onSelect={(status) =>
                setValue('status', Number(status) as COMPANY_ACTIVE_STATUS)
              }
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Agent</AttrText>
            <AgentDropdown
              selected={state.agent || NOT_ASSIGNED_LABEL}
              name={`company-create-agent-dropdown`}
              onSelect={(agent) => setValue('agent', agent)}
              onClear={() => setValue('agent', null)}
            />
          </PropertyRow>
          <PropertyRow>
            <AttrText className="property-label">Key</AttrText>
            <AttrTextInput
              name={'key'}
              value={inputs.key}
              placeholder="Account's key"
              onChange={onChange}
            />
          </PropertyRow>
        </Flex>
      </ContentStyle>
      <Space h="22px" />
      <Flex j="flex-end" style={{ marginRight: '32px' }}>
        <Button onClick={onSave} isLoading={loading} sizeType="m">
          Save
        </Button>
      </Flex>
    </ModalStyle>
  );
};

export default CreateCompanyModal;

const ModalStyle = styled(FadeDiv)`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: 0;
  ${detailModalStyle}
`;

const ContentStyle = styled.div`
  padding: 28px 32px 0 32px;

  .property-label {
    width: 90px;
    height: 24px;
    line-height: 24px;
  }

  .company-selector-wrapper {
    height: 38px;
    position: relative;
    cursor: pointer;
    background-color: ${(props) => props.theme.colors.gray100};
    transition: all 0.2s;
    padding: 0 11px;
    border-radius: 4px;
    .edit-icon {
      opacity: 0;
      transform: 0.2s;
    }
    &:hover {
      background-color: ${(props) => props.theme.colors.gray200};
      .edit-icon {
        opacity: 1;
      }
    }
  }

  .company-property-area {
    flex-direction: column;
    gap: 5px;

    .property-label {
      width: 100px;
      height: 24px;
      line-height: 24px;
    }
  }
`;

const PropertyRow = styled(Flex).attrs({
  g: '10px',
  a: 'center',
})`
  height: 32px;
`;
